// Phase 9.1.B — Client portal dashboard.
//
// Phase 9.2.B (2026-05-11 #2): Recent orders + Active projects cards
// driven by /portal/orders (single dashboard-preview query).
// Phase 9.4.B (2026-05-11 #3): Invoices card driven by /portal/invoices.
// Phase 9.3.B (2026-05-12): Brand assets card now live — shows asset
// count + link to /brand-assets. Last placeholder retired.
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";
import {
  STAGE_LABEL,
  orderStatusWords,
  STAGE_PILL_CLASS,
  fetchPortalOrders,
  formatDate,
  formatUsd,
} from "@/lib/portal-orders";
import { fetchPortalBrandAssets } from "@/lib/portal-brand-assets";
import { fetchPortalAttention } from "@/lib/portal-attention";
import { PORTAL_QUOTES_KEY, fetchPortalQuotes } from "@/lib/portal-quotes";

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function DashboardPage() {
  const { user } = useAuth();

  // Pull the recent 10 orders — enough for the card preview + the
  // "active projects" count derived below. Refetches respect the
  // QueryClient's staleTime (60s) — fine for a dashboard.
  const { data: ordersData } = useQuery({
    queryKey: ["portal-orders", "dashboard-preview"],
    queryFn: () => fetchPortalOrders({ limit: 10 }),
  });
  // (v1's separate open-invoices query retired — the attention payload
  // below carries the open invoices WITH the netted outstanding figure.)
  // Brand assets preview — just need a count for the dashboard card.
  const { data: brandAssetsData } = useQuery({
    queryKey: ["portal-brand-assets", "dashboard-count"],
    queryFn: () => fetchPortalBrandAssets({ limit: 100 }),
  });
  // Portal v2 Slice A — everything awaiting the client, in one call.
  // Proofs to review + invoices with money still owed (deposit- and
  // payments-netted `outstanding`, never the raw balance_due).
  const { data: attention, isPending: attentionPending } = useQuery({
    queryKey: ["portal-attention"],
    queryFn: fetchPortalAttention,
  });
  // Quotes waiting for approval + presentations not yet opened (2026-10-19).
  // Review opens the same page the email link opens.
  const { data: quotesData } = useQuery({
    queryKey: PORTAL_QUOTES_KEY,
    queryFn: fetchPortalQuotes,
  });

  if (!user) return null;
  const firstName = user.name.split(" ")[0] ?? user.name;

  const rows = ordersData?.rows ?? [];
  const recent = rows.slice(0, 3);
  const activeProjectIds = new Set(
    rows
      .filter(
        (r) =>
          r.statusStage === "committed" || r.statusStage === "in_production",
      )
      .map((r) => r.projectId),
  );

  const attentionProofs = attention?.proofs ?? [];
  const attentionInvoices = attention?.invoices ?? [];
  const attentionQuotes = (quotesData?.quotes ?? []).filter((q) => q.status === "awaiting_approval");
  const attentionPresentations = (quotesData?.presentations ?? []).filter((p) => p.status === "ready_to_review");
  const needsYouCount =
    attentionProofs.length + attentionQuotes.length + attentionPresentations.length + attentionInvoices.length;
  // Netted outstanding across open invoices — the same figure the pay
  // buttons charge (deposit + ledgered payments subtracted), never the
  // raw balance_due.
  const outstandingBalance = attentionInvoices
    .reduce((sum, inv) => sum + Number.parseFloat(inv.outstanding || "0"), 0)
    .toFixed(2);
  const brandAssetCount = brandAssetsData?.rows.length ?? 0;

  const steps = [
    ...attentionProofs.map((p) => ({
      key: `proof-${p.id}`,
      title: `Your ${p.projectName} proof is ready.`,
      detail: `Proof v${p.version} · order #${p.orderNumber} · sent ${formatDate(p.sentAt)}. Review it before we move ahead.`,
      action: "Review proof",
      to: `/orders/${p.orderId}#proofs`,
    })),
    ...attentionQuotes.map((q) => ({
      key: `quote-${q.id}`,
      title: `Quote #${q.orderNumber} is waiting for your approval.`,
      detail: `${q.projectName} · sent ${formatDate(q.sentAt)}`,
      action: "Review",
      to: q.reviewUrl,
      external: true,
    })),
    ...attentionPresentations.map((p) => ({
      key: `pres-${p.id}`,
      title: `Presentation #${p.presentationNumber} is ready to review.`,
      detail: `${p.projectName} · sent ${formatDate(p.sentAt)}`,
      action: "Review",
      to: p.reviewUrl,
      external: true,
    })),
    ...attentionInvoices.map((inv) => ({
      key: `inv-${inv.id}`,
      title: `Invoice #${inv.invoiceNumber} is open.`,
      detail: `${formatUsd(inv.outstanding)} due · ${inv.projectName} · issued ${formatDate(inv.invoiceDate)}`,
      // Only promise payment when the invoice can be paid online (CEO
      // 2026-10-19) — card off → the page explains how to pay instead.
      action: inv.canPayOnline ? "View & pay" : "View invoice",
      to: `/invoices/${inv.id}`,
    })),
  ];

  return (
    <PortalLayout>
      <header className="mb-7 mt-2 flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-[0.6875rem] uppercase tracking-[0.12em] text-muted">Your workspace</p>
          <h1 className="mb-2.5 mt-2 text-[2rem] font-semibold leading-[1.15] tracking-[-1px] text-ink">
            {needsYouCount > 0 ? "Good things are in motion." : `${timeOfDayGreeting()}, ${firstName}.`}
          </h1>
          <p className="text-sm text-muted">Your projects, approvals, and next steps — all here.</p>
        </div>
        <Link to="/request-project" className="inline-flex h-10 items-center rounded-md bg-ink px-[0.875rem] text-sm font-medium text-white hover:bg-ink-700">
          Start a project
        </Link>
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(240px,1fr)]">
        <section className="rounded-md border border-rule bg-white p-[1.375rem]" aria-label="Needs your attention">
          <div className="mb-3.5 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">A little input from you.</h2>
            {needsYouCount > 0 && (
              <span className="rounded bg-warn-bg px-2 py-1 text-[0.6875rem] text-warn">
                {needsYouCount} next {needsYouCount === 1 ? "step" : "steps"}
              </span>
            )}
          </div>
          {attentionPending ? (
            <div className="space-y-3" aria-busy="true">
              <div className="h-12 animate-pulse rounded bg-canvas" />
              <div className="h-12 animate-pulse rounded bg-canvas" />
            </div>
          ) : steps.length === 0 ? (
            <p className="text-sm text-muted">Nothing needs you right now. We'll let you know when a quote, proof or invoice is ready.</p>
          ) : (
            <ul>
              {steps.map((st) => (
                <li key={st.key} className="flex items-center justify-between gap-4 border-b border-[#e8e8e5] py-4 first:pt-0 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{st.title}</p>
                    <p className="mt-1 text-xs text-muted">{st.detail}</p>
                  </div>
                  {"external" in st && st.external ? (
                    // Quote / presentation: the emailed page, same tab.
                    <a href={st.to} className="inline-flex min-h-[40px] shrink-0 items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas">
                      {st.action}
                    </a>
                  ) : (
                    <Link to={st.to} className="inline-flex min-h-[40px] shrink-0 items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas">
                      {st.action}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-md border border-rule bg-white p-[1.375rem]">
          <h2 className="mb-3.5 text-lg font-semibold tracking-[-0.4px] text-ink">Your team at other</h2>
          <p className="text-sm text-muted">Questions, ideas, or a change of plans? Keep the conversation with your project.</p>
          <Link to="/messages" className="mt-4 inline-flex min-h-[40px] items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas">
            Send a message
          </Link>
          <p className="mt-5 border-t border-[#eee] pt-4 text-xs text-muted">
            {brandAssetCount > 0 ? (
              <>
                {brandAssetCount} brand {brandAssetCount === 1 ? "asset" : "assets"} on file ·{" "}
                <Link to="/brand-assets" className="text-ink underline underline-offset-4 hover:text-muted">Brand assets</Link>
              </>
            ) : (
              <Link to="/brand-assets" className="text-ink underline underline-offset-4 hover:text-muted">Add your logos and brand files</Link>
            )}
            {Number(outstandingBalance) > 0 ? <span className="mt-1 block">Open balance {formatUsd(outstandingBalance)}</span> : null}
          </p>
        </section>
      </div>

      <section className="mt-6">
        <div className="mb-3.5 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Your orders</h2>
          <Link to="/orders" className="inline-flex min-h-[40px] items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas">
            View all orders
          </Link>
        </div>
        <div className="relative overflow-x-auto rounded-md border border-rule bg-white">
          <table className="w-full whitespace-nowrap text-left">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-[0.6875rem] text-muted">
              <tr>
                <th scope="col" className="px-[1.125rem] py-[0.9375rem] font-medium">Project</th>
                <th scope="col" className="px-[1.125rem] py-[0.9375rem] font-medium">Status</th>
                <th scope="col" className="px-[1.125rem] py-[0.9375rem] font-medium">In hands</th>
                <th scope="col" className="px-[1.125rem] py-[0.9375rem] text-right font-medium">Total</th>
                <th scope="col" className="px-[1.125rem] py-[0.9375rem]"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody className="text-[0.8125rem]">
              {!ordersData ? (
                <tr><td colSpan={5} className="px-[1.125rem] py-[0.9375rem] text-muted">Loading your orders…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-[1.125rem] py-[0.9375rem] text-muted">Your orders will appear here as your account manager sets them up.</td></tr>
              ) : (
                rows.slice(0, 6).map((r) => (
                  <tr key={r.id} className="border-b border-[#e7e7e4] last:border-0">
                    <td className="px-[1.125rem] py-[0.9375rem]">
                      <span className="font-medium text-ink">{r.projectName}</span>
                      <span className="block text-[0.6875rem] text-muted">Order #{r.orderNumber}</span>
                    </td>
                    <td className="px-[1.125rem] py-[0.9375rem]">
                      <span className={"inline-block rounded px-2 py-1 text-[0.6875rem] " + STAGE_PILL_CLASS[r.statusStage]}>{orderStatusWords(r.statusStage, r.statusState)}</span>
                    </td>
                    <td className="px-[1.125rem] py-[0.9375rem] tabular-nums">{formatDate(r.inHandsDate)}</td>
                    <td className="px-[1.125rem] py-[0.9375rem] text-right tabular-nums">{formatUsd(r.totalAmount)}</td>
                    <td className="px-[1.125rem] py-[0.9375rem] text-right">
                      <Link to={`/orders/${r.id}`} className="inline-flex min-h-[32px] items-center rounded border border-rule bg-white px-2.5 text-xs text-ink hover:bg-canvas">View order</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </PortalLayout>
  );
}
